import { useCallback, useRef, useState, type MutableRefObject, type ReactNode } from 'react';

import { Button } from './Button.js';

/**
 * Tracks which of the two save buttons the user pressed so that a form's submit
 * handler can decide whether to close after saving, and so the correct button
 * shows the loading spinner.
 *
 * `closeAfterSaveRef` is a ref (not state) because it must be readable
 * synchronously inside the native form `submit` handler that fires immediately
 * after the button's `click` handler — a state update would not be committed in
 * time.
 */
export interface SaveMode {
  /** Read inside submit handlers: `true` = close the form after a successful save. */
  closeAfterSaveRef: MutableRefObject<boolean>;
  /** Which save button is currently in-flight, for spinner display. */
  pending: 'save' | 'close' | null;
  /** Mark the plain "Save" action (keep the form open). */
  markSave: () => void;
  /** Mark the "Save and close" action. */
  markSaveAndClose: () => void;
  /** Clear the pending display state (call when the modal opens/closes). */
  reset: () => void;
}

export function useSaveMode(): SaveMode {
  const closeAfterSaveRef = useRef(true);
  const [pending, setPending] = useState<'save' | 'close' | null>(null);

  const markSave = useCallback(() => {
    closeAfterSaveRef.current = false;
    setPending('save');
  }, []);
  const markSaveAndClose = useCallback(() => {
    closeAfterSaveRef.current = true;
    setPending('close');
  }, []);
  const reset = useCallback(() => setPending(null), []);

  return { closeAfterSaveRef, pending, markSave, markSaveAndClose, reset };
}

interface FormActionsProps {
  /** When set, the save buttons submit the form with this id (`type="submit"`). */
  formId?: string;
  /** For forms without a `<form>` element: called after marking the save mode. */
  onSubmit?: () => void;
  saveMode: SaveMode;
  /** Whether a save mutation is currently in flight. */
  isPending: boolean;
  onCancel: () => void;
  /** Disable both save buttons (e.g. invalid form). */
  disabled?: boolean;
  saveLabel?: string;
  saveAndCloseLabel?: string;
  cancelLabel?: string;
  /** Left-aligned content, e.g. a Delete button (rendered with `mr-auto`). */
  leading?: ReactNode;
  /** Extra content rendered just before the Cancel button (e.g. "Run now"). */
  extra?: ReactNode;
}

/**
 * Standard modal footer actions: an optional leading slot (Delete), then
 * Cancel, "Save" (save and keep the form open) and "Save and close".
 */
export function FormActions({
  formId,
  onSubmit,
  saveMode,
  isPending,
  onCancel,
  disabled = false,
  saveLabel = 'Save',
  saveAndCloseLabel = 'Save and close',
  cancelLabel = 'Cancel',
  leading,
  extra,
}: FormActionsProps): JSX.Element {
  const submitProps = (mark: () => void): Record<string, unknown> =>
    formId
      ? { type: 'submit', form: formId, onClick: mark }
      : {
          type: 'button',
          onClick: () => {
            mark();
            onSubmit?.();
          },
        };

  return (
    <>
      {leading}
      {extra}
      <Button variant="secondary" onClick={onCancel} disabled={isPending}>
        {cancelLabel}
      </Button>
      <Button
        variant="secondary"
        {...submitProps(saveMode.markSave)}
        isLoading={isPending && saveMode.pending === 'save'}
        disabled={disabled || isPending}
      >
        {saveLabel}
      </Button>
      <Button
        {...submitProps(saveMode.markSaveAndClose)}
        isLoading={isPending && saveMode.pending === 'close'}
        disabled={disabled || isPending}
      >
        {saveAndCloseLabel}
      </Button>
    </>
  );
}
